import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Camera, MapPin, Star, Calendar, MessageCircle, Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PhotographerProfile = () => {
  const { id } = useParams();

  const photographer = {
    id: 1,
    name: 'Sarah Johnson',
    specialty: 'Wedding Photography',
    location: 'New York, NY',
    rating: 4.9,
    reviews: 127,
    bio: 'With over 10 years of experience capturing life\'s most precious moments, I specialize in creating timeless wedding memories. My style blends photojournalism with artistic portraiture to tell your unique love story.',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
    coverImage: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1200&h=400&fit=crop',
    portfolio: [
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=800&h=600&fit=crop',
    ],
    packages: [
      {
        name: 'Essentials',
        price: 2500,
        duration: '6 hours',
        features: ['Up to 6 hours coverage', '300+ edited photos', 'Online gallery', '1 photographer'],
      },
      {
        name: 'Premium',
        price: 4500,
        duration: '10 hours',
        features: ['Up to 10 hours coverage', '600+ edited photos', 'Online gallery', '2 photographers', 'Engagement shoot'],
        popular: true,
      },
      {
        name: 'Ultimate',
        price: 7000,
        duration: 'Full day',
        features: ['Unlimited coverage', '1000+ edited photos', 'Premium albums', '2 photographers', 'Engagement shoot', 'Same-day preview'],
      },
    ],
    reviews: [
      {
        name: 'Emily & David',
        rating: 5,
        date: 'June 2024',
        text: 'Sarah was absolutely incredible! She captured our special day perfectly. Every photo tells a story and we couldn\'t be happier.',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      },
      {
        name: 'Michael Thompson',
        rating: 5,
        date: 'May 2024',
        text: 'Professional, creative, and so easy to work with. Sarah made us feel comfortable throughout the entire day.',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/auth">
                <Button variant="ghost" size="sm" data-testid="header-signin">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="px-6 md:px-12 lg:px-24 py-6">
        <Link to="/photographers" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Photographers
        </Link>
      </div>

      {/* Cover Image */}
      <div className="relative h-80 mb-8">
        <img
          src={photographer.coverImage}
          alt="Cover"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="px-6 md:px-12 lg:px-24 pb-24">
        {/* Profile Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12 -mt-24 relative z-10">
          <img
            src={photographer.image}
            alt={photographer.name}
            className="w-32 h-32 rounded-full object-cover border-4 border-background shadow-xl"
            data-testid="photographer-avatar"
          />
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="font-playfair text-4xl font-bold mb-2" data-testid="photographer-name">{photographer.name}</h1>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <Badge variant="secondary" data-testid="photographer-specialty">{photographer.specialty}</Badge>
                  <div className="flex items-center text-muted-foreground">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span data-testid="photographer-location">{photographer.location}</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                    <span className="font-semibold mr-1" data-testid="photographer-rating">{photographer.rating}</span>
                    <span className="text-muted-foreground" data-testid="photographer-reviews">({photographer.reviews} reviews)</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="lg" data-testid="message-button">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
                <Link to={`/book/${photographer.id}`}>
                  <Button size="lg" data-testid="book-button">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - About & Portfolio */}
          <div className="lg:col-span-2 space-y-12">
            {/* About */}
            <section data-testid="about-section">
              <h2 className="font-playfair text-3xl font-semibold mb-4" data-testid="about-title">About</h2>
              <p className="text-lg leading-relaxed text-muted-foreground" data-testid="about-text">{photographer.bio}</p>
            </section>

            {/* Portfolio */}
            <section data-testid="portfolio-section">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="portfolio-title">Portfolio</h2>
              <div className="masonry-grid">
                {photographer.portfolio.map((image, index) => (
                  <div key={index} className="masonry-item" data-testid={`portfolio-image-${index}`}>
                    <img
                      src={image}
                      alt={`Portfolio ${index + 1}`}
                      className="w-full rounded-sm cursor-pointer hover:opacity-90 transition-opacity"
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Reviews */}
            <section data-testid="reviews-section">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="reviews-title">Reviews</h2>
              <div className="space-y-6">
                {photographer.reviews.map((review, index) => (
                  <Card key={index} data-testid={`review-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="font-semibold" data-testid={`review-name-${index}`}>{review.name}</div>
                              <div className="text-sm text-muted-foreground" data-testid={`review-date-${index}`}>{review.date}</div>
                            </div>
                            <div className="flex">
                              {[...Array(review.rating)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              ))}
                            </div>
                          </div>
                          <p className="text-muted-foreground" data-testid={`review-text-${index}`}>{review.text}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Packages */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <h2 className="font-playfair text-3xl font-semibold mb-6" data-testid="packages-title">Packages</h2>
              <div className="space-y-4">
                {photographer.packages.map((pkg, index) => (
                  <Card
                    key={index}
                    className={pkg.popular ? 'border-primary shadow-lg' : ''}
                    data-testid={`package-${index}`}
                  >
                    <CardContent className="p-6">
                      {pkg.popular && (
                        <Badge className="mb-4" data-testid={`package-badge-${index}`}>Most Popular</Badge>
                      )}
                      <h3 className="font-playfair text-2xl font-semibold mb-2" data-testid={`package-name-${index}`}>{pkg.name}</h3>
                      <div className="font-playfair text-4xl font-bold mb-1" data-testid={`package-price-${index}`}>
                        ${pkg.price.toLocaleString()}
                      </div>
                      <div className="text-sm text-muted-foreground mb-4" data-testid={`package-duration-${index}`}>{pkg.duration}</div>
                      <ul className="space-y-2 mb-6">
                        {pkg.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start text-sm" data-testid={`package-feature-${index}-${fIndex}`}>
                            <Check className="w-4 h-4 mr-2 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link to={`/book/${photographer.id}?package=${pkg.name.toLowerCase()}`}>
                        <Button className="w-full" variant={pkg.popular ? 'default' : 'outline'} data-testid={`package-book-button-${index}`}>
                          Select Package
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotographerProfile;